import { z } from 'zod';
/**
 * All domain enums in one place — kept in sync with the Prisma schema
 * (apps/api/prisma/schema.prisma) and CLAUDE.md §5.
 *
 * NOTE: the values use `snake_case` to match what we will store in PostgreSQL
 * enums. The Prisma schema must mirror these exactly.
 */
// ───────────────────────── User ─────────────────────────
export const UserRoleSchema = z.enum(['customer', 'admin', 'support']);
/**
 * Lifecycle states for a user account.
 *
 * `review` is the key state for USP #4 ("Pause-don't-Freeze"): the account is
 * not fully suspended; standing orders still flow, the user can still consult
 * history, but new outgoing transfers are held.
 */
export const UserStatusSchema = z.enum([
    'pending_email',
    'pending_kyc',
    'active',
    'review',
    'suspended',
    'closed',
]);
/**
 * Subscription tiers. Canonical pricing (CLAUDE.md §2): Free / Plus 7,99€ /
 * Pro 19,99€ / Business 49,99€. `plus` landed in Phase 2.
 */
export const PlanSchema = z.enum(['free', 'plus', 'pro', 'business']);
// ───────────────────────── KYC ─────────────────────────
export const KycStatusSchema = z.enum([
    'not_started',
    'pending',
    'approved',
    'rejected',
    'requires_more_info',
]);
/**
 * Tiered KYC (Phase 2 decision #3):
 *  - `light`: plan Free — email + name + DoB + Sumsub Basic liveness selfie.
 *  - `full` : plan Plus/Pro/Business — full Sumsub (ID + proof of address +
 *    selfie + PEP/sanctions screening). An upgrade Free→paid promotes light→full.
 */
export const KycTierSchema = z.enum(['light', 'full']);
/**
 * Risk tier from the AML risk-based approach (5AMLD/6AMLD).
 * See COMPLIANCE-PLAYBOOK §4.1.
 */
export const RiskLevelSchema = z.enum(['low', 'medium', 'high']);
// ───────────────────────── Account (Swan) ─────────────────────────
/**
 * `review` is again the USP #4 state — partial-functionality review,
 * not a full suspension.
 */
export const AccountStatusSchema = z.enum([
    'opening',
    'opened',
    'review',
    'suspended',
    'closed',
]);
// ───────────────────────── Swan account holder ─────────────────────────
export const SwanHolderTypeSchema = z.enum(['individual', 'company']);
export const SwanHolderStatusSchema = z.enum(['opening', 'verified', 'suspended', 'closed']);
// ───────────────────────── Card ─────────────────────────
export const CardTypeSchema = z.enum(['virtual', 'physical']);
export const CardStatusSchema = z.enum(['pending', 'enabled', 'frozen', 'canceled']);
// ───────────────────────── Transaction ─────────────────────────
/**
 * Mirrors `TransactionType` in apps/api/prisma/schema.prisma — keep them in sync.
 * Swan's 42 transaction types fold into these 21 (see `swan-mappers.ts`).
 */
export const TransactionTypeSchema = z.enum([
    'sepa_credit_transfer_in',
    'sepa_credit_transfer_out',
    'sepa_credit_transfer_return',
    'internal_transfer_in',
    'internal_transfer_out',
    'internal_transfer_return',
    'international_transfer_in',
    'international_transfer_out',
    'international_transfer_return',
    'direct_debit_in',
    'direct_debit_out',
    'direct_debit_return',
    'card_payment',
    'card_authorization',
    'card_refund',
    'card_reversal',
    'check_in',
    'check_return',
    'fee',
    'fx_conversion',
    'other',
]);
export const TransactionSideSchema = z.enum(['credit', 'debit']);
export const TransactionStatusSchema = z.enum(['pending', 'booked', 'rejected', 'canceled']);
// ───────────────────────── Account Review (USP #1) ─────────────────────────
export const AccountReviewTypeSchema = z.enum([
    'kyc_refresh',
    'transaction_review',
    'aml_check',
    'fraud_check',
    'other',
]);
export const AccountReviewStatusSchema = z.enum(['in_progress', 'resolved', 'escalated']);
export const AccountReviewTriggerSchema = z.enum(['automated', 'manual', 'swan_webhook']);
// ───────────────────────── Risk Assessment (USP #2) ─────────────────────────
/** Outcome of the pre-flight risk assessment on a pending transaction. */
export const RiskAssessmentOutcomeSchema = z.enum([
    'pre_approved',
    'requires_review',
    'rejected',
    'escalated',
]);
/** Status of an intra-EU FX conversion (USP #3, Phase 4). */
export const FxConversionStatusSchema = z.enum(['pending', 'completed', 'rejected', 'reversed']);
/**
 * Flags raised by the risk assessment engine. Mirrors COMPLIANCE-PLAYBOOK §4.2.
 * Add new flags as the engine grows.
 */
export const RiskFlagSchema = z.enum([
    'unusual_amount',
    'sensitive_country',
    'new_beneficiary',
    'high_frequency',
    'round_amount_structuring',
    'just_below_threshold',
    'peak_unusual_time',
]);
// ───────────────────────── Swan onboarding KYC (étape 12) ─────────────────────────
/**
 * EXACT Swan enum values, sourced from a LIVE introspection of the Swan
 * sandbox-partner GraphQL schema (not from memory). These are Swan's own
 * constants (PascalCase) — distinct from our snake_case domain enums — and Swan
 * is the source of truth. If a value here ever diverges from the live schema,
 * the live schema wins. Documented in SWAN-SCHEMA-VALIDATION.md §6.
 */
export const SwanEmploymentStatusSchema = z.enum([
    'Craftsman',
    'Employee',
    'Entrepreneur',
    'Farmer',
    'Manager',
    'Practitioner',
    'Retiree',
    'ShopOwner',
    'Student',
    'Unemployed',
]);
export const SwanMonthlyIncomeSchema = z.enum([
    'LessThan500',
    'Between500And1500',
    'Between1500And3000',
    'Between3000And4500',
    'MoreThan4500',
]);
// ───────────────────────── Webhook ─────────────────────────
export const WebhookSourceSchema = z.enum(['swan', 'sumsub']);
// ───────────────────────── Audit log ─────────────────────────
export const AuditActorTypeSchema = z.enum([
    'user',
    'admin',
    'system',
    'swan_webhook',
    'sumsub_webhook',
]);
// ───────────────────────── Security events (Phase 2.5) ─────────────────────────
export const SecuritySeveritySchema = z.enum(['info', 'warning', 'high', 'critical']);
//# sourceMappingURL=enums.js.map